import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Download,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Loader2,
  FileSpreadsheet,
  Users,
  Award,
  Building2,
  FolderKanban,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useImportTechs,
  useImportTechCertifications,
  useImportClients,
  useImportProjects,
  useDownloadPilotTemplate,
  parseCSVPreview,
  groupImportErrors,
  generateErrorReport,
  type PilotEntityType,
  type PilotImportOptions,
  type ImportResponse,
  type GroupedErrors,
} from '@/hooks/use-import';

const ENTITY_TYPES: {
  id: PilotEntityType;
  label: string;
  description: string;
  icon: React.ElementType;
  requiredColumns: string;
}[] = [
  {
    id: 'techs',
    label: 'Technicians',
    description: 'Import technician profiles (first_name, last_name, email required)',
    icon: Users,
    requiredColumns: 'first_name, last_name, email',
  },
  {
    id: 'tech-certifications',
    label: 'Tech Certifications',
    description: 'Import certifications linked to existing techs by email',
    icon: Award,
    requiredColumns: 'tech_email, cert_type, issue_date',
  },
  {
    id: 'clients',
    label: 'Clients',
    description: 'Import client/company records',
    icon: Building2,
    requiredColumns: 'name',
  },
  {
    id: 'projects',
    label: 'Projects',
    description: 'Import projects optionally linked to clients',
    icon: FolderKanban,
    requiredColumns: 'project_number, name',
  },
];

export function DataImportPage() {
  const navigate = useNavigate();
  const { canManageOrganization } = usePermissions();

  const [entityType, setEntityType] = useState<PilotEntityType>('techs');
  const [file, setFile] = useState<File | null>(null);
  const [updateMode, setUpdateMode] = useState<PilotImportOptions['updateMode']>('upsert');
  const [validationResult, setValidationResult] = useState<ImportResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [csvPreview, setCsvPreview] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [expandedErrorGroups, setExpandedErrorGroups] = useState<Set<string>>(new Set());

  const importTechs = useImportTechs();
  const importCerts = useImportTechCertifications();
  const importClients = useImportClients();
  const importProjects = useImportProjects();
  const downloadTemplate = useDownloadPilotTemplate(entityType);

  const getImportMutation = () => {
    switch (entityType) {
      case 'techs': return importTechs;
      case 'tech-certifications': return importCerts;
      case 'clients': return importClients;
      case 'projects': return importProjects;
    }
  };

  const entityConfig = ENTITY_TYPES.find((e) => e.id === entityType)!;

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setValidationResult(null);
    setImportResult(null);
    try {
      const text = await selectedFile.text();
      const preview = parseCSVPreview(text, 5);
      setCsvPreview(preview);
    } catch {
      toast.error('Failed to read file');
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv'))) {
        handleFileSelect(droppedFile);
      } else {
        toast.error('Please upload a CSV file');
      }
    },
    [handleFileSelect]
  );

  const handleValidate = useCallback(async () => {
    if (!file) return;
    setIsValidating(true);
    try {
      const mutation = getImportMutation();
      const result = await mutation.mutateAsync({
        file,
        options: { dryRun: true, updateMode },
      });
      setValidationResult(result);
      if (result.summary.errors === 0) {
        toast.success('Validation passed', {
          description: `${result.summary.created} to create, ${result.summary.updated} to update, ${result.summary.skipped} to skip`,
        });
      } else {
        toast.error('Validation found errors', {
          description: `${result.summary.errors} row(s) have errors`,
        });
      }
    } catch {
      toast.error('Validation failed');
    } finally {
      setIsValidating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, updateMode, entityType]);

  const handleImport = useCallback(async () => {
    if (!file) return;
    try {
      const mutation = getImportMutation();
      const result = await mutation.mutateAsync({
        file,
        options: { dryRun: false, updateMode },
      });
      setImportResult(result);
      if (result.summary.errors === 0) {
        toast.success('Import complete', {
          description: `${result.summary.created} created, ${result.summary.updated} updated`,
        });
      } else {
        toast.error('Import completed with errors', {
          description: `${result.summary.errors} row(s) had errors — no records were committed`,
        });
      }
    } catch {
      toast.error('Import failed');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, updateMode, entityType]);

  const handleDownloadErrors = useCallback(() => {
    const results = importResult?.results || validationResult?.results;
    if (!results) return;
    const report = generateErrorReport(results);
    if (!report) return;
    const blob = new Blob([report], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${entityType}_import_errors.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [importResult, validationResult, entityType]);

  const handleReset = useCallback(() => {
    setFile(null);
    setCsvPreview(null);
    setValidationResult(null);
    setImportResult(null);
    setExpandedErrorGroups(new Set());
  }, []);

  const toggleErrorGroup = (message: string) => {
    setExpandedErrorGroups((prev) => {
      const next = new Set(prev);
      if (next.has(message)) {
        next.delete(message);
      } else {
        next.add(message);
      }
      return next;
    });
  };

  const activeResult = importResult || validationResult;
  const groupedErrors: GroupedErrors[] = activeResult ? groupImportErrors(activeResult.results) : [];
  const importMutation = getImportMutation();

  if (!canManageOrganization) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-sm text-red-800">Only administrators and managers can import data.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Settings
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">
            Import Pilot Data
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">
            Migrate your existing data from spreadsheets
          </p>
        </div>
      </div>

      {/* Entity type selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {ENTITY_TYPES.map((et) => {
          const Icon = et.icon;
          return (
            <Card
              key={et.id}
              className={`cursor-pointer transition-colors ${
                entityType === et.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                  : 'hover:border-gray-300'
              }`}
              onClick={() => {
                setEntityType(et.id);
                handleReset();
              }}
            >
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4" />
                  <span className="font-medium text-sm">{et.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{et.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Upload & Options */}
        <div className="lg:col-span-2 space-y-4">
          {/* Template download */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                CSV Template
              </CardTitle>
              <CardDescription>
                Required columns: <code className="text-xs bg-muted px-1 py-0.5 rounded">{entityConfig.requiredColumns}</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate.mutate()}
                disabled={downloadTemplate.isPending}
              >
                <Download className="h-4 w-4 mr-1" />
                Download Template
              </Button>
            </CardContent>
          </Card>

          {/* File upload */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Upload CSV</CardTitle>
            </CardHeader>
            <CardContent>
              {!file ? (
                <div
                  className="border-2 border-dashed rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => document.getElementById('csv-input')?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Drop your CSV here or click to browse
                  </p>
                  <input
                    id="csv-input"
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileSelect(f);
                    }}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium">{file.name}</span>
                      <Badge variant="outline">{csvPreview?.headers.length || 0} columns</Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleReset}>
                      Change file
                    </Button>
                  </div>

                  {csvPreview && csvPreview.headers.length > 0 && (
                    <div className="overflow-x-auto border rounded max-h-48">
                      <table className="text-xs w-full">
                        <thead className="bg-muted">
                          <tr>
                            {csvPreview.headers.map((h) => (
                              <th key={h} className="px-2 py-1 text-left font-medium">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreview.rows.slice(0, 3).map((row, i) => (
                            <tr key={i} className="border-t">
                              {row.map((cell, j) => (
                                <td key={j} className="px-2 py-1 truncate max-w-[120px]">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Options */}
          {file && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Import Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium w-32">Update mode</label>
                  <Select
                    value={updateMode}
                    onValueChange={(v) => setUpdateMode(v as PilotImportOptions['updateMode'])}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upsert">Upsert (create or update)</SelectItem>
                      <SelectItem value="insert_only">Insert only (skip existing)</SelectItem>
                      <SelectItem value="update_only">Update only (skip new)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3">
                  <Button onClick={handleValidate} disabled={isValidating || importMutation.isPending}>
                    {isValidating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Validating...
                      </>
                    ) : (
                      'Validate'
                    )}
                  </Button>

                  {validationResult && validationResult.summary.errors === 0 && !importResult && (
                    <Button
                      variant="default"
                      onClick={handleImport}
                      disabled={importMutation.isPending}
                    >
                      {importMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-1" />
                          Import {validationResult.summary.created + validationResult.summary.updated} rows
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: Results */}
        <div className="space-y-4">
          {activeResult && (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {importResult ? 'Import Results' : 'Validation Preview'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {activeResult.summary.created > 0 && (
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                        <span>{activeResult.summary.created} to create</span>
                      </div>
                    )}
                    {activeResult.summary.updated > 0 && (
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />
                        <span>{activeResult.summary.updated} to update</span>
                      </div>
                    )}
                    {activeResult.summary.skipped > 0 && (
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5 text-yellow-600" />
                        <span>{activeResult.summary.skipped} skipped</span>
                      </div>
                    )}
                    {activeResult.summary.errors > 0 && (
                      <div className="flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                        <span>{activeResult.summary.errors} errors</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {groupedErrors.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base text-red-700">Errors</CardTitle>
                      <Button variant="ghost" size="sm" onClick={handleDownloadErrors}>
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Error report
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {groupedErrors.map((group) => (
                      <div key={group.message} className="border rounded p-2">
                        <button
                          className="flex items-center gap-1 w-full text-left text-sm"
                          onClick={() => toggleErrorGroup(group.message)}
                        >
                          {expandedErrorGroups.has(group.message) ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          <span className="flex-1 text-red-700">{group.message}</span>
                          <Badge variant="destructive" className="text-xs">
                            {group.count}
                          </Badge>
                        </button>
                        {expandedErrorGroups.has(group.message) && (
                          <p className="text-xs text-muted-foreground mt-1 pl-5">
                            Rows: {group.rows.join(', ')}
                          </p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {importResult && importResult.summary.errors === 0 && (
                <Card className="border-green-200 bg-green-50 dark:bg-green-950">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Import successful</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
